import callApi, { CallApiResult, getCustomerId } from 'src/api';
import CustomerMailAddress from 'src/entities/customer/CustomerMailAddress';
import {
    SCHEMA_CUSTOMER_MAIL_ADDRESS_SHAPE,
    SCHEMA_CUSTOMER_PHONE_NUMBER_SHAPE,
    SCHEMA_MY_DATA_SHAPE,
    SCHEMA_PASSWORD_RESET_SHAPE,
} from 'src/schemas/MyData';

/* Customer Mail Addresses */

export const fetchCustomerMailAddresses = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/customer/' + customerId + '/mailAddress';
    return callApi(endpoint);
};

export const fetchCustomerMailAddressById = (mailAddressId: string): Promise<CallApiResult<CustomerMailAddress>> => {
    const endpoint = `/api/customer/mailAddress/${mailAddressId}`;
    return callApi(endpoint);
};

export const submitCustomerMailAddress = (data: SCHEMA_CUSTOMER_MAIL_ADDRESS_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi('/api/customer/' + customerId + '/mailAddress', { method: 'POST', data });
};

export const assignCustomerMailAddress = (
    type: 'account' | 'newsletter',
    id: string = 'null'
): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/mailAddress/${type}/${id}`, { method: 'POST' });
};

export const deleteCustomerMailAddress = (id: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/mailAddress/${id}`, { method: 'DELETE' });
};

/* Customer Phone Numbers */

export const fetchCustomerPhoneNumbers = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/customer/' + customerId + '/phoneNumber';
    return callApi(endpoint);
};

export const submitCustomerPhoneNumber = (data: SCHEMA_CUSTOMER_PHONE_NUMBER_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi('/api/customer/' + customerId + '/phoneNumber', { method: 'POST', data });
};

export const assignCustomerPhoneNumber = (id: string = 'null'): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/phoneNumber/account/${id}`, { method: 'POST' });
};

export const deleteCustomerPhoneNumber = (id: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/phoneNumber/${id}`, { method: 'DELETE' });
};

/* Customer Contact Settings (atomic apply, PM #28/#29) */

export interface ContactSettingsAddressAssignment {
    addressId: number;
    /** Full replacement set; must contain at least one id when provided. */
    invoiceMailAddressIds?: string[];
    /** Full replacement set; must contain at least one id when provided. */
    deliveryMailAddressIds?: string[];
    userPhoneNumberId?: string;
}

export interface ContactSettingsRequest {
    accountMailAddressId?: string;
    currentPassword?: string;
    accountPhoneNumberId?: string;
    /** Full replacement set of newsletter recipients; [] unsubscribes all. */
    newsletterMailAddressIds?: string[];
    addressAssignments?: ContactSettingsAddressAssignment[];
}

export const applyContactSettings = (data: ContactSettingsRequest): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/contactSettings`, { method: 'POST', data });
};

export const replaceCustomerMailAddress = (oldId: string, newId: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/mailAddress/${oldId}/replaceWith/${newId}`, { method: 'PUT' });
};

export const replaceCustomerPhoneNumber = (oldId: string, newId: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/phoneNumber/${oldId}/replaceWith/${newId}`, { method: 'PUT' });
};

/* Customer Data */

export const fetchCustomerData = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();

    const endpoint = '/api/customer/' + customerId + '/myData';
    return callApi(endpoint);
};

export const submitMyDataForm = (raw: SCHEMA_MY_DATA_SHAPE): Promise<CallApiResult> => {
    const data = {
        ...raw,
    };
    const customerId = getCustomerId();
    return callApi('/api/customer/' + customerId + '/myData', { method: 'PATCH', data });
};

export interface MyDataPatch {
    firstName?: string;
    lastName?: string;
    company?: string;
    languageIso2?: string;
    timeZone?: string;
}

/** Partial myData PATCH: absent keys stay unchanged, a blank company clears it. */
export const patchMyData = (data: MyDataPatch): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi('/api/customer/' + customerId + '/myData', { method: 'PATCH', data });
};

export const fetchCreditHistory = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/creditHistory`);
};

/** R59: registration points waiting for an e-mail confirmation (own bonus, referred customers). */
export const fetchPendingRegistrationPoints = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/pendingRegistrationPoints`);
};

export const fetchReferrals = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/referrals`);
};

/* Customer Avatar */

export const uploadCustomerAvatar = (file: File): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const data = new FormData();
    data.append('file', file);
    return callApi(`/api/customer/${customerId}/avatar`, { method: 'POST', data });
};

export const deleteCustomerAvatar = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    return callApi(`/api/customer/${customerId}/avatar`, { method: 'DELETE' });
};

/* Resend Confirmation Email */
export const resendConfirmationEmail = (emailAddressId: string): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = `/api/customer/${customerId}/mailAddress/${emailAddressId}/resendConfirmationToken`;
    return callApi(endpoint, { method: 'POST' });
};

export const submitPasswordResetForm = (data: SCHEMA_PASSWORD_RESET_SHAPE): Promise<CallApiResult> => {
    return callApi('/api/customer/current/resetPassword', { method: 'PUT', data });
};
